/*
 * 头部组件：从工单顶部到检查清单之前
 * 基础信息 / 前次设备维护详情 / 确认以上信息 / 变更实施单 / 子单激活 / 打卡 / 人脸识别 / 设备核对 / 上传设备照片
 * （开始维护已移至中部检查清单）
 * 依赖：common/core.js ui.js biz.js
 */
export default function installHeader(MW, host) {
  var h = MW.h, ui = MW.ui, biz = MW.biz, util = MW.util, store = MW.store, api = MW.api;
  var v = biz.v;

  var checking = false; // 打卡请求中
  var scanning = false; // 扫码比对中

  /* ---------- 条件（与原 schema x-reactions 一致） ---------- */
  var C = {
    lock: function () { return biz.isClosed(); },
    subStartValid: function () { return biz.needSubStart() ? !!v("sub_start") : true; },
    /** sub_ticket_check_in：子单已激活 && 已确认信息 && 未打卡 */
    checkinEditable: function () { return !C.lock() && C.subStartValid() && !!v("confirm_abnormal") && !v("sub_ticket_check_in"); },
    faceEditable: function () { return !C.lock() && !!v("sub_ticket_check_in") && !biz.started(); },
    scanFailed: function () { return biz.scanFailed(); },
    showScan: function () { return biz.isSelf() && !biz.isWeak(); },
    showUploadStart: function () { return biz.showUploadStart(); },
  };

  /* ---------- 交互逻辑 ---------- */
  function onConfirmAbnormal(val) {
    store.set("confirm_abnormal", val);
    biz.inputUpdate("confirm_abnormal", val);
  }

  /** Location 打卡：宿主获取定位并写入签到记录 -> sub_ticket_check_in -> write_sub_ticket_check_in 回填 check_in_dc */
  function doCheckin() {
    if (!host.locate) return ui.alert("当前环境不支持打卡，请在企业微信手机端完成打卡确认");
    // 打卡需服务器识别所在园区并记录签到，必须联网
    if (MW.net.offline) return MW.offline.needNet("打卡");
    checking = true;
    var unhold = MW.sync && MW.sync.on ? MW.sync.hold(["sub_ticket_check_in", "check_in_dc"]) : function () {};
    store.touch("sub_ticket_check_in");
    var ctx = biz.ctx();
    var prev = v("sub_ticket_check_in");
    Promise.resolve(host.locate({ user: ctx.user, taskId: ctx.taskId, ticketId: biz.ticketId() }))
      .then(function (value) {
        store.set("sub_ticket_check_in", value);
        return api.call("write_sub_ticket_check_in", { sub_ticket_check_in: value }).then(function (resp) {
          var data = resp && resp.data;
          if (data && data.result) {
            store.set("check_in_dc", data.result.check_in_dc);
            // 接口已写入流程变量，这里再按项记录一次操作人，便于他人端提示"xxx 更新了：打卡"
            biz.inputUpdate("sub_ticket_check_in", value);
            biz.inputUpdate("check_in_dc", data.result.check_in_dc);
          } else ui.alert("未查询到数据");
        }, function (e) {
          store.set("sub_ticket_check_in", prev || "");
          throw e;
        });
      })
      .catch(function (e) {
        if (e && e.cancel) return ui.toast("打卡已取消", "info");
        if (util.isNetErr(e)) return MW.offline.needNet("打卡");
        console.error("[mw] 打卡失败", e);
        ui.alert("打卡失败" + (util.errMsg(e) ? "：" + util.errMsg(e) : ""));
      })
      .then(function () {
        unhold();
        checking = false;
        store.touch("sub_ticket_check_in");
      });
  }

  /** Scanner：扫码完成 / 输入失焦时触发。
   *  比对规则与后端 check_qr_code 完全一致，直接在本机得出结论（不依赖网络），
   *  结果与 qr_code / qr_number 一并走同步队列，没网时暂存，联网后补传 */
  function onScan(val) {
    store.set("scan_qr_code", val);
    biz.inputUpdate("scan_qr_code", val);
    if (!val) {
      store.set("upload_photo_time", "记录照片上传时间");
      return;
    }
    var q = MW.offline.qr(val, v("device_number"));
    var t = util.now();
    store.patch({ scan_code_result: q.result, qr_code: val, qr_number: q.number, upload_photo_time: t });
    ["scan_code_result", "qr_code", "qr_number", "upload_photo_time"].forEach(function (k) { biz.inputUpdate(k, v(k)); });
    if (!q.ok) ui.toast("扫描到的设备编号与本单不一致，请核对后重新扫码或上传设备照片", "warn");
  }

  function onUploadStart(list) {
    store.set("upload_start", list);
    // 原逻辑：Upload 值为数组（含空数组）即记录时间并保存 upload_start（原 upload_start_img 同样只写该变量）
    var t = util.now();
    store.set("upload_photo_time", t);
    biz.inputUpdate("upload_photo_time", t);
    biz.inputUpdate("upload_start", list);
  }

  /* ---------- 渲染 ---------- */
  function hero() {
    return biz.hero({
      crumbs: [v("maintenance_type") || "维护", v("maintenance_cycle")],
      title: v("process_title") || ((v("mozu_name") || "") + "-" + (v("device_name") || "") + "维护工单"),
      chips: [v("maintenance_process_type"), v("professional")],
    });
  }

  function basicInfo() {
    return biz.infoPanel([
      ["主单号", "main_ticket_id", "ri-file-list-3-line"], ["园区", "campus_name", "ri-building-2-line"], ["模组", "mozu_name", "ri-layout-grid-line"],
      ["设备类型", "devicetypes_name", "ri-cpu-line"], ["设备编号", "device_number", "ri-barcode-line", true], ["维护类型", "maintenance_type", "ri-shield-check-line"],
      ["维护周期", "maintenance_cycle", "ri-repeat-line"], ["计划开始日期", "plan_start_date", "ri-calendar-event-line"], ["计划结束日期", "plan_end_date", "ri-calendar-check-line"],
    ]);
  }

  function lastDetail(ctx) {
    var list = v("sub_check_list") || [];
    var links = [["上次维护子单号", "last_ticket_id"], ["问题跟进单号", "p_ticket_id"], ["维修单号", "event_id"]].filter(function (l) { return v(l[1]); });
    var locked = !!v("sub_ticket_check_in") || C.lock();
    return ui.panel(
      { title: "前次设备维护详情", icon: "ri-history-line", id: "mw-last", extra: list.length ? ui.tag("异常 " + list.length + " 项", "warn", "ri-alarm-warning-line") : ui.tag("无异常", "ok") },
      ui.field({ label: "上次维护工单详情" }, ui.text(v("sub_check_info"), { pre: true })),
      v("ai_abnormal_summary") && h("div", { class: "mw-ai" }, h("div", { class: "mw-ai__badge" }, h("i", { class: "ri-sparkling-2-fill" }), "AI"), h("p", null, v("ai_abnormal_summary"))),
      links.length && ui.grid(3, links.map(function (l) { return ui.field({ label: l[0] }, ui.link(biz.ticketUrl(v(l[1])), v(l[1]))); })),
      list.length > 0 &&
        ui.rtable({
          cls: "mw-rtable--last",
          columns: [
            { title: "序号", cls: "is-idx", render: function (r, i) { return h("span", { class: "mw-idx" }, i + 1); }, mobileHead: true },
            { title: "检查项", key: "check_item_name", mobileFull: true },
            { title: "异常部位", key: "abnormal_area" },
            { title: "异常描述", key: "exception_description" },
            { title: "是否影响运行", render: function (r) { return ui.tag(r.running_status || "—", r.running_status === "是" ? "danger" : "muted"); } },
            { title: "原因分析", key: "reason_analysis", mobileFull: true },
          ],
          rows: list,
        }),
      biz.switchRow({
        title: "确认以上信息", required: true, key: "confirm_abnormal", icon: "ri-checkbox-multiple-line",
        time: v("confirm_abnormal") ? "已确认前次维护详情" : "请阅读前次维护详情后确认",
        value: !!v("confirm_abnormal"), disabled: locked, onChange: onConfirmAbnormal, error: ctx.err("confirm_abnormal"),
        tip: v("sub_ticket_check_in") && !C.lock() ? "已打卡，不可修改" : null,
      })
    );
  }

  function changeLink() {
    var arr = Array.isArray(v("change_child_id")) ? v("change_child_id") : [];
    var cid = arr[0] || "", taskId = arr[1];
    if (!cid) return null;
    var k = util.hostKind(), href = "";
    if (k === "woa") href = String(v("ticket_done_woa") || "") + encodeURIComponent(cid);
    else if (k === "tencent") href = String(v("ticket_done_keji") || "") + encodeURIComponent(cid);
    else if (k === "qq") {
      href = String(v("change_move_url") || "") + encodeURIComponent(cid);
      if (taskId) href += (href.indexOf("?") > -1 ? "&" : "?") + "taskId=" + encodeURIComponent(taskId);
    }
    return ui.field({ label: "变更实施单号", desc: "可跳转至变更实施单操作" }, ui.link(href, cid, { icon: "ri-git-branch-line" }));
  }

  function checkinBlock(ctx) {
    var val = v("sub_ticket_check_in");
    var mobile = util.isMobileUA();
    var editable = C.checkinEditable() && mobile;
    var dc = v("check_in_dc");
    var far = dc === "未定位到所在园区";
    var hint = !mobile ? "▶ 打卡仅限移动端使用，请在企业微信手机端完成打卡确认"
      : !C.subStartValid() ? "请先在随工单激活子单"
      : !v("confirm_abnormal") ? "请先确认前次维护详情" : "维护开始前点击打卡";
    return h(
      "div",
      { class: ["mw-checkin", val && "is-done", far && "is-far", ctx.err("sub_ticket_check_in") && "has-error"], dataset: { key: "sub_ticket_check_in" } },
      h("div", { class: "mw-checkin__btn-wrap" },
        h("button", { type: "button", class: ["mw-checkin__btn", checking && "is-busy"], disabled: !editable || checking, onClick: doCheckin },
          h("i", { class: checking ? "ri-loader-4-line mw-spin" : val ? "ri-map-pin-user-fill" : "ri-map-pin-2-line" }),
          h("b", null, checking ? "定位中" : val ? "已打卡" : "打卡"),
          val && h("small", null, String(val.SignTime || "").slice(11, 16))
        )
      ),
      h(
        "div",
        { class: "mw-checkin__info" },
        h("b", null, h("i", { class: "mw-req" }, "*"), "打卡确认"),
        val
          ? [
              h("div", { class: "mw-kv" }, h("span", null, "打卡地址"), h("b", null, val.Address || "—")),
              h("div", { class: "mw-kv" }, h("span", null, "打卡时间"), h("b", null, val.SignTime || "—")),
              h("div", { class: "mw-kv" }, h("span", null, "打卡园区"), far ? ui.tag(dc, "danger", "ri-error-warning-line") : dc ? ui.tag(dc, "ok", "ri-map-pin-line") : ui.tag("识别中", "muted")),
            ]
          : h("p", { class: ["mw-checkin__hint", !mobile && "is-warn"] }, hint),
        ctx.err("sub_ticket_check_in") && h("div", { class: "mw-field__error" }, h("i", { class: "ri-error-warning-fill" }), ctx.err("sub_ticket_check_in"))
      )
    );
  }

  function prepare(ctx) {
    var showSub = biz.needSubStart();
    if (!showSub && v("sub_start") !== true) store.set("sub_start", true, { silent: true });
    var link = changeLink();
    return ui.panel(
      { title: "维护准备", icon: "ri-map-pin-time-line", id: "mw-prepare", sub: "打卡 · 身份核验 · 设备核对" },
      (link || showSub) &&
        ui.grid(2, [
          link,
          showSub &&
            ui.field({ label: "子单激活", desc: "请先在随工单激活子单，再开始操作维护工单！", descWarn: !v("sub_start") },
              h("div", { class: "mw-inline" }, ui.switch({ value: !!v("sub_start"), disabled: true }), v("sub_start") ? ui.tag("已激活", "ok") : ui.tag("待随工单激活", "warn", "ri-time-line"))),
        ]),
      checkinBlock(ctx),
      biz.isSelf() &&
        biz.faceSection({
          suffix: "", title: "维护开始前，请拍照进行人脸识别", editable: C.faceEditable(), qualMethod: "get_qualification",
          errors: { face_photo: ctx.err("face_photo") }, lockTip: biz.started() ? "已开始维护，不可修改" : "完成打卡后可拍照上传",
        }),
      deviceCheck(ctx)
    );
  }

  /** 解析设备二维码，规则同后端 SubTicketStatus.check_qr_code：
   *  "{'DevC': 编号, 'DevN': 名称}" 取 DevC 作为设备编号；非字典串则整串即编号。仅用于展示对照，比对结论见 MW.offline.qr */
  function parseQr(raw) {
    var s = String(raw == null ? "" : raw).trim();
    if (!s) return null;
    if (s.charAt(0) === "{" && s.charAt(s.length - 1) === "}") {
      var pick = function (k) {
        var m = s.match(new RegExp("[\"']" + k + "[\"']\\s*:\\s*[\"']([^\"']*)[\"']"));
        return m ? m[1].trim() : "";
      };
      return { code: pick("DevC"), name: pick("DevN") };
    }
    return { code: s, name: "" };
  }

  /** 扫码比对结果：后端结论 + 扫描编号 / 本单编号并排对照 */
  function scanResult(res, ok) {
    if (scanning) return ui.tag("比对中…", "info", "ri-loader-4-line mw-spin");
    var q = parseQr(v("scan_qr_code"));
    if (util.isEmpty(res) && !q) return ui.text("", { placeholder: "扫码后自动比对" });
    var target = v("device_number") || "";
    var state = util.isEmpty(res) ? "is-wait" : ok ? "is-ok" : "is-fail";
    var icon = state === "is-ok" ? "ri-checkbox-circle-fill" : state === "is-fail" ? "ri-close-circle-fill" : "ri-error-warning-fill";
    var same = !!(q && q.code) && q.code === target;
    return h(
      "div",
      { class: ["mw-scanres", state] },
      h("div", { class: "mw-scanres__head" }, h("i", { class: icon }), h("span", null, res || "未获取到比对结果，请重新扫码")),
      q &&
        h(
          "div",
          { class: "mw-scanres__cmp" },
          h(
            "div",
            { class: ["mw-scanres__row", same ? "is-same" : "is-diff"] },
            h("label", null, "扫描编号"),
            h("b", null, q.code || "未识别到设备编号"),
            q.name && h("small", null, q.name)
          ),
          h("div", { class: "mw-scanres__row" }, h("label", null, "本单编号"), h("b", null, target || "—"))
        )
    );
  }

  function deviceCheck(ctx) {
    var showScan = C.showScan();
    var showUp = C.showUploadStart();
    var res = v("scan_code_result");
    var ok = String(res || "").indexOf("匹配成功") === 0;
    var q = parseQr(v("scan_qr_code"));
    var swOn = biz.started();
    var locked = swOn || C.lock();
    return h(
      "div",
      { class: "mw-device" },
      h("div", { class: "mw-face__title" }, h("i", { class: "ri-qr-scan-2-line" }), h("h3", null, biz.isSelf() ? "维护开始前，请核对设备编号，上传设备照片" : "维护开始前，请上传设备照片")),
      showScan &&
        ui.grid(2, [
          ui.field({ label: "扫描设备二维码", required: true, key: "scan_qr_code", error: ctx.err("scan_qr_code"), desc: "目标设备：" + (v("device_number") || "—") },
            biz.scanner({ number: q && q.code, name: q && q.name, state: C.scanFailed() ? "fail" : ok ? "ok" : "", disabled: locked || scanning, onChange: onScan })),
          ui.field({ label: "设备二维码扫描结果" }, scanResult(res, ok)),
        ]),
      ui.grid(2, [
        ui.field({ label: "设备照片上传时间" }, ui.text(v("upload_photo_time"), { placeholder: "【上传照片】记录照片上传时间" })),
        showUp &&
          ui.field({ label: "上传设备照片", required: true, key: "upload_start", error: ctx.err("upload_start"), desc: "仅支持.png/.jpg/.jpeg格式上传" },
            ui.upload({ value: v("upload_start"), maxCount: 2, accept: "image/*", capture: "environment", text: "拍照上传", hint: "请拍摄设备照片及设备二维码照片，最多 2 张", disabled: locked, onChange: onUploadStart })),
      ])
    );
  }

  /* ---------- 浮动按钮：回到上次填写的位置 / 检查清单全部展开·收起 ---------- */
  function folds() {
    return (MW.partList || []).filter(function (p) { return p.el && p.fold; });
  }
  function foldState() {
    return folds().reduce(function (a, p) {
      var s = p.fold.state();
      a.total += s.total;
      a.open += s.open;
      return a;
    }, { total: 0, open: 0 });
  }
  function viewBox() {
    var sc = util.scroller();
    if (sc) { var r = sc.getBoundingClientRect(); return [r.top, r.bottom]; }
    return [0, window.innerHeight || document.documentElement.clientHeight];
  }
  /** 当前屏幕上方正在看的步骤卡片（批量折叠后以它为基准保持位置） */
  function stepInView() {
    var vb = viewBox(), list = [];
    folds().forEach(function (p) { list = list.concat([].slice.call(p.el.querySelectorAll(".mw-step[id]"))); });
    for (var i = 0; i < list.length; i++) {
      var r = list[i].getBoundingClientRect();
      if (r.bottom > vb[0] + 60) return r.top < vb[1] ? list[i] : null;
    }
    return null;
  }
  function toggleFold() {
    var s = foldState();
    if (!s.total) return;
    var open = s.open < s.total; // 未全部展开 -> 全部展开；已全部展开 -> 全部收起
    var el = stepInView();
    var id = el && el.id;
    if (id) MW.anchor.sel = "#" + id;
    folds().forEach(function (p) { p.fold.setAll(open); });
    store.touch();
    ui.toast((open ? "已展开全部 " : "已收起全部 ") + s.total + " 个步骤", "info");
    if (id) {
      // 收起后卡片变短，若标题已在屏幕上方则把它带回视野
      setTimeout(function () {
        var n = MW.$("#" + id);
        if (n && n.getBoundingClientRect().top < viewBox()[0] + 56) util.scrollTo(util.pageTop(n) - 64, true);
      }, 80);
    }
  }
  function onLocate() {
    var key = biz.lastAnchor();
    var sel = key && '[data-key="' + key + '"]';
    var el = sel && MW.anchor.find(sel);
    if (!el && sel && MW.partList.some(function (p) { return p.reveal && p.reveal(key); })) {
      // 所在步骤已折叠：先展开再定位
      return setTimeout(function () {
        var n = MW.anchor.find(sel);
        if (n) MW.anchor.reveal(n);
      }, 60);
    }
    if (el) return MW.anchor.reveal(el);
    var pos = v("last_position") || sessionStorage.getItem("last_position");
    if (!pos) return ui.toast("暂无上次填写位置", "warn");
    util.scrollTo(Number(pos), true);
  }
  function mountDock() {
    if (MW.dock) return;
    var icon = h("i", { class: "ri-expand-up-down-line" });
    var badge = h("span", { class: "mw-fab__badge" });
    var foldBtn = h("button", { type: "button", class: "mw-fab mw-fab--fold", onClick: toggleFold }, icon, badge);
    var dock = (MW.dock = MW.addFloating(h("div", { class: "mw-fab-dock" },
      !biz.isEnded() && h("button", { type: "button", class: "mw-fab", "data-tip": "点击按钮回到上次填写的位置", onClick: onLocate }, h("i", { class: "ri-map-pin-time-line" })),
      foldBtn
    )));
    // 图标 / 角标表达当前状态：全部展开（实心，图标为"收起"）/ 全部收起（图标为"展开"）/ 部分展开（角标 已展开/总数）
    function paintFold() {
      var s = foldState();
      foldBtn.style.display = s.total ? "" : "none";
      if (!s.total) return;
      var st = s.open === s.total ? "open" : s.open === 0 ? "closed" : "mixed";
      var tip = st === "open" ? "已全部展开 · 点击全部收起" : st === "closed" ? "已全部收起 · 点击全部展开" : "已展开 " + s.open + "/" + s.total + " 个步骤 · 点击全部展开";
      foldBtn.className = "mw-fab mw-fab--fold is-" + st;
      foldBtn.setAttribute("data-tip", tip);
      foldBtn.setAttribute("aria-label", tip);
      icon.className = st === "open" ? "ri-contract-up-down-line" : "ri-expand-up-down-line";
      badge.textContent = st === "mixed" ? s.open + "/" + s.total : "";
      badge.style.display = st === "mixed" ? "" : "none";
    }
    MW.onFoldChange = paintFold;
    var off = store.on("*", paintFold);
    // 屏幕宽度跨过手机 / PC 分界时（旋转、调窄窗口）按新终端的默认规则重绘
    var mql = window.matchMedia("(max-width: 760px)");
    var onMq = function () { store.touch(); };
    if (mql.addEventListener) mql.addEventListener("change", onMq);
    else if (mql.addListener) mql.addListener(onMq);
    paintFold();
    MW.cleanups.push(function () {
      off();
      if (mql.removeEventListener) mql.removeEventListener("change", onMq);
      else if (mql.removeListener) mql.removeListener(onMq);
      MW.onFoldChange = null;
      MW.dock = null;
    });
    return dock;
  }

  biz.part({
    name: "header",
    title: "头部",
    render: function (ctx) {
      return h("div", { class: "mw-header" }, hero(), biz.closedBanner(), basicInfo(), lastDetail(ctx), prepare(ctx));
    },
    mounted: function () { mountDock(); },
    errors: function () {
      var e = [];
      biz.req(e, "confirm_abnormal", v("confirm_abnormal"), "请确认以上信息");
      biz.req(e, "sub_ticket_check_in", v("sub_ticket_check_in"), "请完成打卡");
      if (biz.isSelf()) {
        biz.req(e, "face_photo", v("face_photo"), "请拍照上传进行人脸识别");
        if (!biz.isWeak()) biz.req(e, "scan_qr_code", v("scan_qr_code"), "请扫描设备二维码");
      }
      if (C.showUploadStart()) biz.req(e, "upload_start", v("upload_start"), "请上传设备照片");
      return e;
    },
  });
}
